import React, { useEffect, useRef, useState } from 'react';
import { User, Settings, CreditCard, BookOpen, Mail, LogOut } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FirstStepsTutorial } from '@/components/tutorial/FirstStepsTutorial';
import { DailyNavigationTutorial } from '@/components/tutorial/DailyNavigationTutorial';
import { ProfileAccessAppearanceTutorial } from '@/components/tutorial/ProfileAccessAppearanceTutorial';
import { TeamCommunicationTutorial } from '@/components/tutorial/TeamCommunicationTutorial';
import { AdminActionsTutorial } from '@/components/tutorial/AdminActionsTutorial';
import {
  CURRENT_TUTORIAL_EXPERIENCE_VERSION,
  DEFAULT_ONBOARDING,
  loadOnboardingState,
  saveOnboardingState,
} from '@/lib/onboardingSync';

// Pantalla informativa tras salir de un tutorial dentro del onboarding:
// muestra dónde volver a encontrar los tutoriales (Cuenta -> Tutoriales).
function AccountTutorialsVisual({ onEntendido }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Tus tutoriales siguen disponibles</DialogTitle>
        <DialogDescription>
          Siempre podrás acceder a ellos desde el botón de Cuenta.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between border-b border-slate-200 pb-3">
            <div className="text-xl font-bold text-slate-900">RUTAPP</div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-400 bg-white ring-4 ring-amber-400 ring-offset-2">
              <User className="h-5 w-5 text-slate-900" />
            </div>
          </div>

          <div className="mt-3 space-y-2 rounded-md border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-700">
              <Settings className="h-4 w-4 text-slate-500" />
              Ajustes generales
            </div>
            <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-700">
              <CreditCard className="h-4 w-4 text-slate-500" />
              Ver planes y precios
            </div>
            <div className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-slate-900 ring-4 ring-amber-400">
              <BookOpen className="h-4 w-4 text-slate-700" />
              Tutoriales
            </div>
            <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-700">
              <Mail className="h-4 w-4 text-slate-500" />
              Contactar con soporte
            </div>
            <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-700">
              <LogOut className="h-4 w-4 text-slate-500" />
              Cerrar sesión
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Siempre podrás acceder a ellos desde el botón de Cuenta.
        </p>

        <Button type="button" onClick={onEntendido} className="w-full">
          Entendido
        </Button>
      </div>
    </>
  );
}

// BLOQUE 5: recordatorio único posterior al abandonar el onboarding.
// Todo el acceso a Firestore pasa por onboardingSync (nunca Firebase directo).
export function OnboardingFlow({ uid, userRole }) {
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'welcome' | 'tutorial' | 'abandon-visual' | 'reminder'
  const [dismissedThisSession, setDismissedThisSession] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [onboarding, setOnboarding] = useState(DEFAULT_ONBOARDING);
  const [activeTutorial, setActiveTutorial] = useState(null);

  const mountedRef = useRef(true);
  const isAdmin = userRole !== 'worker';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!uid) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const state = await loadOnboardingState(uid);
        if (cancelled) return;

        setOnboarding(state.onboarding);
        const versionSeen = state.onboarding.tutorialExperienceVersionSeen ?? 0;
        const reminderVersionShown =
          state.onboarding.tutorialExperienceReminderVersionShown ?? 0;

        if (versionSeen < CURRENT_TUTORIAL_EXPERIENCE_VERSION && !dismissedThisSession) {
          setPhase('welcome');
        } else if (
          versionSeen >= CURRENT_TUTORIAL_EXPERIENCE_VERSION &&
          state.onboarding.onboardingDismissed === true &&
          reminderVersionShown < CURRENT_TUTORIAL_EXPERIENCE_VERSION &&
          !dismissedThisSession
        ) {
          setPhase('reminder');
        } else {
          setPhase('idle');
        }
      } catch (err) {
        console.error('[OnboardingFlow] Error loading onboarding state:', err);
        if (!cancelled) setPhase('idle');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const handleStartVideos = async () => {
    setActionError(null);
    setSaving(true);
    try {
      const nextOnboarding = {
        ...onboarding,
        tutorialExperienceVersionSeen: CURRENT_TUTORIAL_EXPERIENCE_VERSION,
        tutorialExperienceReminderVersionShown: CURRENT_TUTORIAL_EXPERIENCE_VERSION,
      };
      await saveOnboardingState(uid, nextOnboarding);
      if (mountedRef.current) {
        setOnboarding(nextOnboarding);
        setPhase('idle');
        window.dispatchEvent(
          new CustomEvent('rutapp:openTutorialHub', {
            detail: { section: 'videos' },
          })
        );
      }
    } catch (err) {
      console.error('[OnboardingFlow] Error al iniciar videotutoriales:', err);
      if (mountedRef.current) {
        setActionError('No hemos podido continuar. Inténtalo de nuevo.');
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleStartInteractive = async () => {
    setActionError(null);
    setSaving(true);
    try {
      const nextOnboarding = {
        ...onboarding,
        tutorialExperienceVersionSeen: CURRENT_TUTORIAL_EXPERIENCE_VERSION,
        tutorialExperienceReminderVersionShown: CURRENT_TUTORIAL_EXPERIENCE_VERSION,
      };
      await saveOnboardingState(uid, nextOnboarding);
      if (mountedRef.current) {
        setOnboarding(nextOnboarding);
        setActiveTutorial('primeros-pasos');
        setPhase('tutorial');
      }
    } catch (err) {
      console.error('[OnboardingFlow] Error al iniciar tutorial interactivo:', err);
      if (mountedRef.current) {
        setActionError('No hemos podido iniciar el recorrido. Inténtalo de nuevo.');
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleNotNow = async () => {
    setActionError(null);
    setSaving(true);
    try {
      const nextOnboarding = {
        ...onboarding,
        onboardingDismissed: true,
        tutorialExperienceVersionSeen: CURRENT_TUTORIAL_EXPERIENCE_VERSION,
      };
      await saveOnboardingState(uid, nextOnboarding);
      if (mountedRef.current) {
        setOnboarding(nextOnboarding);
        setDismissedThisSession(true);
        setPhase('idle');
      }
    } catch (err) {
      console.error('[OnboardingFlow] Error dismissing onboarding:', err);
      if (mountedRef.current) {
        setActionError('No hemos podido guardar. Inténtalo de nuevo.');
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  // Helper local: une el id completado (sin duplicados), persiste y solo
  // avanza (nextId) o cierra (nextId null) tras exito.
  const completeAndAdvance = async (completedId, nextId, openHub = false) => {
    setActionError(null);
    setSaving(true);
    try {
      const nextCompleted = Array.from(
        new Set([...onboarding.completedTutorials, completedId])
      );
      const nextOnboarding = { ...onboarding, completedTutorials: nextCompleted };
      await saveOnboardingState(uid, nextOnboarding);
      if (mountedRef.current) {
        setOnboarding(nextOnboarding);
        if (nextId) {
          setActiveTutorial(nextId);
        } else {
          if (openHub) {
            window.dispatchEvent(new CustomEvent('rutapp:openTutorialHub'));
          }
          setPhase('idle');
        }
      }
    } catch (err) {
      console.error('[OnboardingFlow] Error persisting completed tutorial:', err);
      if (mountedRef.current) {
        setActionError('No hemos podido guardar tu progreso. Inténtalo de nuevo.');
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleFirstCompleted = () =>
    completeAndAdvance('primeros-pasos', 'navegacion-gestion-diaria');
  const handleSecondCompleted = () =>
    completeAndAdvance('navegacion-gestion-diaria', 'perfil-accesos-apariencia');
  const handleThirdCompleted = () =>
    completeAndAdvance('perfil-accesos-apariencia', 'comunicacion-entre-usuarios');
  const handleFourthCompleted = () => {
    if (isAdmin) {
      return completeAndAdvance('comunicacion-entre-usuarios', 'acciones-de-administrador');
    }
    // worker: terminar recorrido (temporal; la pantalla final global llegara despues).
    return completeAndAdvance('comunicacion-entre-usuarios', null);
  };
  const handleFifthCompleted = () =>
    completeAndAdvance('acciones-de-administrador', null, true);

  // Salir de un tutorial dentro del onboarding: NO marca completado, conserva
  // completedTutorials y persiste el abandono (onboardingDismissed=true),
  // mostrando la pantalla informativa de dónde encontrar los tutoriales.
  const handleTutorialExit = async () => {
    setActionError(null);
    setSaving(true);
    try {
      const nextOnboarding = {
        ...onboarding,
        onboardingDismissed: true,
        followUpReminderShown: false,
      };
      await saveOnboardingState(uid, nextOnboarding);
      if (mountedRef.current) {
        setOnboarding(nextOnboarding);
        setPhase('abandon-visual');
      }
    } catch (err) {
      console.error('[OnboardingFlow] Error persisting onboarding dismissal:', err);
      if (mountedRef.current) {
        setActionError('No hemos podido guardar tu progreso. Inténtalo de nuevo.');
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleEntendido = () => {
    setPhase('idle');
  };

  // Recordatorio posterior: marca followUpReminderShown y abre el Hub real.
  const handleVerTutoriales = async () => {
    setActionError(null);
    setSaving(true);
    try {
      const nextOnboarding = {
        ...onboarding,
        tutorialExperienceReminderVersionShown: CURRENT_TUTORIAL_EXPERIENCE_VERSION,
      };
      await saveOnboardingState(uid, nextOnboarding);
      if (mountedRef.current) {
        setOnboarding(nextOnboarding);
        setPhase('idle');
        window.dispatchEvent(new CustomEvent('rutapp:openTutorialHub'));
      }
    } catch (err) {
      console.error('[OnboardingFlow] Error persisting reminder preference:', err);
      if (mountedRef.current) {
        setActionError('No hemos podido guardar esta preferencia. Inténtalo de nuevo.');
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleReminderNotNow = async () => {
    setActionError(null);
    setSaving(true);
    try {
      const nextOnboarding = {
        ...onboarding,
        tutorialExperienceReminderVersionShown: CURRENT_TUTORIAL_EXPERIENCE_VERSION,
      };
      await saveOnboardingState(uid, nextOnboarding);
      if (mountedRef.current) {
        setOnboarding(nextOnboarding);
        setPhase('idle');
      }
    } catch (err) {
      console.error('[OnboardingFlow] Error persisting reminder preference:', err);
      if (mountedRef.current) {
        setActionError('No hemos podido guardar esta preferencia. Inténtalo de nuevo.');
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  if (loading || phase === 'idle') {
    return null;
  }

  const isTutorial = phase === 'tutorial';
  const isAbandonVisual = phase === 'abandon-visual';
  const isReminder = phase === 'reminder';

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className={
          isTutorial
            ? 'max-h-[85dvh] overflow-y-auto sm:max-w-lg [&>button]:hidden'
            : 'sm:max-w-md [&>button]:hidden'
        }
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {isTutorial ? (
          <>
            {activeTutorial === 'primeros-pasos' && (
              <FirstStepsTutorial
                onExit={handleTutorialExit}
                completionActionLabel="Seguir con el Tutorial 2"
                onCompletionAction={handleFirstCompleted}
              />
            )}
            {activeTutorial === 'navegacion-gestion-diaria' && (
              <DailyNavigationTutorial
                onExit={handleTutorialExit}
                completionActionLabel="Seguir con el Tutorial 3"
                onCompletionAction={handleSecondCompleted}
              />
            )}
            {activeTutorial === 'perfil-accesos-apariencia' && (
              <ProfileAccessAppearanceTutorial
                onExit={handleTutorialExit}
                completionActionLabel="Seguir con el Tutorial 4"
                onCompletionAction={handleThirdCompleted}
              />
            )}
            {activeTutorial === 'comunicacion-entre-usuarios' && (
              <TeamCommunicationTutorial
                onExit={handleTutorialExit}
                completionActionLabel={
                  isAdmin ? 'Seguir con el Tutorial 5' : 'Finalizar tutoriales'
                }
                onCompletionAction={handleFourthCompleted}
              />
            )}
            {activeTutorial === 'acciones-de-administrador' && (
              <AdminActionsTutorial
                onExit={handleTutorialExit}
                completionActionLabel="Volver a Tutoriales"
                onCompletionAction={handleFifthCompleted}
              />
            )}
            {actionError ? (
              <p className="text-sm text-red-500">{actionError}</p>
            ) : null}
          </>
        ) : isAbandonVisual ? (
          <AccountTutorialsVisual onEntendido={handleEntendido} />
        ) : isReminder ? (
          <>
            <DialogHeader>
              <DialogTitle>Tus tutoriales siguen disponibles</DialogTitle>
              <DialogDescription>
                Puedes acceder a todos los tutoriales disponibles para tu cuenta desde el menú de Cuenta.
              </DialogDescription>
            </DialogHeader>

            {actionError ? (
              <p className="text-sm text-red-500">{actionError}</p>
            ) : null}

            <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch">
              <Button
                type="button"
                onClick={handleVerTutoriales}
                disabled={saving}
                className="w-full"
              >
                Ver tutoriales
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleReminderNotNow}
                disabled={saving}
                className="w-full"
              >
                Ahora no
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-center">Aprende a usar Rutapp</DialogTitle>
              <DialogDescription>
                Empieza con nuestros videotutoriales para ver cómo funciona
                Rutapp. Si prefieres practicar paso a paso, también tienes
                tutoriales interactivos.
              </DialogDescription>
            </DialogHeader>

            {actionError ? (
              <p className="text-sm text-red-500">{actionError}</p>
            ) : null}

            <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch">
              <Button
                type="button"
                onClick={handleStartVideos}
                disabled={saving}
                className="w-full"
              >
                Empezar videotutoriales
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleStartInteractive}
                disabled={saving}
                className="w-full"
              >
                Tutorial interactivo
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleNotNow}
                disabled={saving}
                className="w-full"
              >
                Ahora no
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
